import { shortcutFromEvent, deltaFromDom, getSelection } from '@typewriter/view';
import { getLine, SOURCE_USER as SOURCE_USER$3, getLines, Delta } from '@typewriter/editor';

const isMac = navigator.userAgent.indexOf('Macintosh') !== -1;
const modExpr = isMac ? /Cmd/ : /Ctrl/;
function shortcuts(options) {
    return function (editor, root) {
        function onKeyDown(event) {
            var shortcut = shortcutFromEvent(event);
            if (shortcut.length <= 1)
                return;
            var canceled = false;
            var init = {
                detail: shortcut,
                cancelable: true,
                bubbles: options && options.bubbles || false
            };
            var os = isMac ? 'mac' : 'win';
            var osSpecificEvent = new CustomEvent('shortcut:' + os + ':' + shortcut, init);
            var specificEvent = new CustomEvent('shortcut:' + shortcut, init);
            var generalEvent = new CustomEvent('shortcut', init);
            root.dispatchEvent(osSpecificEvent);
            root.dispatchEvent(specificEvent);
            root.dispatchEvent(generalEvent);
            canceled = osSpecificEvent.defaultPrevented || specificEvent.defaultPrevented || generalEvent.defaultPrevented;
            if (modExpr.test(shortcut)) {
                init.detail = shortcut.replace(modExpr, 'Mod');
                specificEvent = new CustomEvent('shortcut:' + init.detail, init);
                generalEvent = new CustomEvent('shortcut', init);
                root.dispatchEvent(specificEvent);
                root.dispatchEvent(generalEvent);
                canceled = canceled || specificEvent.defaultPrevented || generalEvent.defaultPrevented;
            }
            if (canceled) {
                event.preventDefault();
            }
        }
        root.addEventListener('keydown', onKeyDown);
        return {
            options,
            onDestroy() {
                root.removeEventListener('keydown', onKeyDown);
            }
        };
    };
}

const BAD_CHARS = /[^\x09\x0A\x0D\x20-\xFF\x85\xA0-\uD7FF\uE000-\uFDCF\uFDE0-\uFFFD]/gm;
function input() {
    return function (editor, root, paper) {
        function onPaste(event) {
            event.preventDefault();
            const dataTransfer = event.clipboardData;
            if (!dataTransfer || !editor.selection)
                return;
            const html = dataTransfer.getData('text/html');
            if (!html) {
                let text = dataTransfer.getData('text/plain');
                const event = { text };
                text && editor.fire('paste', event);
                text = event.text;
                if (text) {
                    editor.insertText(editor.selection, text);
                }
            }
            else {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                let delta = deltaFromDom(doc.body, paper);
                cleanText(delta);
                const lastOp = delta.ops[delta.ops.length - 1];
                if (lastOp && typeof lastOp.insert === 'string' && lastOp.insert !== '\n') {
                    lastOp.insert = lastOp.insert.replace(/\n$/, '');
                }
                const event = { delta };
                editor.fire('paste', event);
                delta = event.delta;
                editor.insertContent(editor.selection, delta);
            }
        }
        function onEnter(event) {
            if (event.defaultPrevented)
                return;
            event.preventDefault();
            const range = editor.getSelectedRange();
            if (!range)
                return;
            const isCollapsed = range[0] === range[1];
            const line = getLine(editor.contents, range[0]);
            let attributes = line.attributes;
            const block = paper.blocks.findByAttributes(attributes) || paper.blocks.getDefault();
            const isDefault = block === paper.blocks.getDefault();
            const length = line.end - line.start - 1;
            const atEnd = range[1] === line.end - 1;
            const activeFormats = editor.activeFormats;
            if (!length && !isDefault && !block.defaultFollows && isCollapsed) {
                editor.formatLine(range, {}, SOURCE_USER$3);
            }
            else {
                const selection = [range[0] + 1, range[0] + 1];
                if (atEnd && !isDefault && block.defaultFollows) {
                    editor.transaction(() => {
                        const reverse = {};
                        Object.keys(attributes).forEach(key => reverse[key] = null);
                        editor.insertText(range, '\n', attributes, SOURCE_USER$3, selection);
                        editor.updateContents(editor.delta().retain(selection[0]).retain(1, reverse));
                    }, SOURCE_USER$3);
                }
                else {
                    if (typeof block.getNextLineAttributes === 'function') {
                        attributes = block.getNextLineAttributes(attributes);
                    }
                    editor.insertText(range, '\n', attributes, SOURCE_USER$3, selection);
                }
            }
            editor.activeFormats = activeFormats;
        }
        function onShiftEnter(event) {
            if (event.defaultPrevented)
                return;
            event.preventDefault();
            if (!paper.embeds.get('br'))
                return;
            const range = editor.getSelectedRange();
            if (!range)
                return;
            editor.insertEmbed(range, 'br', true, undefined, SOURCE_USER$3);
        }
        function onBackspace(event) {
            if (event.defaultPrevented)
                return;
            event.preventDefault();
            const range = editor.getSelectedRange();
            if (!range)
                return;
            function flattenBlock(line, force) {
                if (!range)
                    return;
                const block = paper.blocks.findByAttributes(line.attributes) || paper.blocks.getDefault();
                if (block.indentable && line.attributes.indent) {
                    onTab(new CustomEvent('shortcut', { detail: 'Shift+Tab' }));
                    return true;
                }
                if (block && (force || block !== paper.blocks.getDefault() && !block.defaultFollows)) {
                    editor.formatLine([range[0], range[0]], {}, SOURCE_USER$3);
                    return true;
                }
            }
            if (range[0] + range[1] === 0) {
                const line = getLine(editor.contents, range[0]);
                if (flattenBlock(line, true))
                    return true;
            }
            else {
                if (range[0] === range[1]) {
                    const line = getLine(editor.contents, range[0]);
                    if (range[0] === line.start && flattenBlock(line)) {
                        return;
                    }
                    range[0]--;
                }
                editor.deleteText(range, SOURCE_USER$3);
            }
        }
        function onDelete(event) {
            if (event.defaultPrevented)
                return;
            event.preventDefault();
            const range = editor.getSelectedRange();
            if (!range)
                return;
            const isCollapsed = range[0] === range[1];
            if (isCollapsed && range[0] >= editor.length - 1)
                return;
            if (isCollapsed) {
                range[1]++;
            }
            editor.deleteText(range, SOURCE_USER$3);
        }
        function onTab(event) {
            if (event.defaultPrevented)
                return;
            event.preventDefault();
            const shortcut = event.detail;
            const direction = shortcut === 'Tab' || shortcut === 'Mod+]' ? 1 : -1;
            const range = editor.getSelectedRange();
            if (!range)
                return;
            const [from, to] = range;
            const lines = getLines(editor.contents, from, to);
            editor.transaction(() => {
                lines.forEach(line => {
                    const block = paper.blocks.findByAttributes(line.attributes) || paper.blocks.getDefault();
                    if (block.indentable) {
                        const indent = (line.attributes.indent || 0) + direction;
                        const range = [line.start, line.start];
                        if (indent < 0) {
                            editor.formatLine(range, {});
                        }
                        else {
                            const attributes = Object.assign(Object.assign({}, line.attributes), { indent });
                            editor.formatLine(range, attributes);
                        }
                    }
                });
            }, SOURCE_USER$3);
        }
        function onInput(event) {
            const selection = getSelection(root, paper);
            if (event.inputType === 'insertText' && editor.selection && event.data) {
                const committed = editor.insertText(editor.selection, event.data, undefined, SOURCE_USER$3, selection);
                if (!committed)
                    editor.render();
            }
            else {
                updateContents();
            }
        }
        function updateContents() {
            const selection = getSelection(root, paper);
            const delta = deltaFromDom(root, paper);
            const change = editor.contents.diff(delta);
            cleanText(change);
            const committed = editor.updateContents(change, SOURCE_USER$3, selection);
            if (!committed)
                editor.render();
        }
        root.addEventListener('input', onInput);
        root.addEventListener('paste', onPaste);
        root.addEventListener('shortcut:Enter', onEnter);
        root.addEventListener('shortcut:Shift+Enter', onShiftEnter);
        root.addEventListener('shortcut:Backspace', onBackspace);
        root.addEventListener('shortcut:Delete', onDelete);
        root.addEventListener('shortcut:Tab', onTab);
        root.addEventListener('shortcut:Shift+Tab', onTab);
        root.addEventListener('shortcut:Mod+]', onTab);
        root.addEventListener('shortcut:Mod+[', onTab);
        return {
            onDestroy() {
                root.removeEventListener('input', onInput);
                root.removeEventListener('paste', onPaste);
                root.removeEventListener('shortcut:Enter', onEnter);
                root.removeEventListener('shortcut:Shift+Enter', onShiftEnter);
                root.removeEventListener('shortcut:Backspace', onBackspace);
                root.removeEventListener('shortcut:Delete', onDelete);
                root.removeEventListener('shortcut:Tab', onTab);
                root.removeEventListener('shortcut:Shift+Tab', onTab);
                root.removeEventListener('shortcut:Mod+]', onTab);
                root.removeEventListener('shortcut:Mod+[', onTab);
            }
        };
    };
}
function cleanText(delta) {
    delta.forEach(op => {
        if (typeof op.insert === 'string') {
            op.insert = op.insert.replace(BAD_CHARS, '').replace(/[ \xa0]*\n[ \xa0]*/g, '\n');
        }
    });
}

const SOURCE_USER = 'user';
const keymap = {
    'Mod+B': editor => editor.toggleTextFormat(editor.selection, { bold: true }, SOURCE_USER),
    'Mod+I': editor => editor.toggleTextFormat(editor.selection, { italic: true }, SOURCE_USER),
    'Mod+1': editor => editor.toggleLineFormat(editor.selection, { header: 1 }, SOURCE_USER),
    'Mod+2': editor => editor.toggleLineFormat(editor.selection, { header: 2 }, SOURCE_USER),
    'Mod+3': editor => editor.toggleLineFormat(editor.selection, { header: 3 }, SOURCE_USER),
    'Mod+4': editor => editor.toggleLineFormat(editor.selection, { header: 4 }, SOURCE_USER),
    'Mod+5': editor => editor.toggleLineFormat(editor.selection, { header: 5 }, SOURCE_USER),
    'Mod+6': editor => editor.toggleLineFormat(editor.selection, { header: 6 }, SOURCE_USER),
    'Mod+0': editor => editor.formatLine(editor.selection, {}, SOURCE_USER),
};
function keyShortcuts(customShortcuts = {}) {
    return (editor, root) => {
        const shortcuts = Object.assign(Object.assign({}, keymap), customShortcuts);
        function onShortcut(event) {
            if (event.defaultPrevented)
                return;
            const shortcut = event.detail;
            if (shortcut in shortcuts) {
                event.preventDefault();
                shortcuts[shortcut](editor);
            }
        }
        root.addEventListener('shortcut', onShortcut);
        return {
            onDestroy() {
                root.addEventListener('shortcut', onShortcut);
            }
        };
    };
}

const SOURCE_USER$1 = 'user';
const SOURCE_SILENT = 'silent';
function history({ maxStack = 500, delay = 0, stack = newStack() } = {}) {
    return function (editor, root) {
        let lastRecorded = 0;
        let lastAction = '';
        let ignoreChange = false;
        function undo(event) {
            action(event, 'undo', 'redo');
        }
        function redo(event) {
            action(event, 'redo', 'undo');
        }
        function hasUndo() {
            return stack.undo.length > 0;
        }
        function hasRedo() {
            return stack.redo.length > 0;
        }
        function cutoff() {
            lastRecorded = 0;
        }
        function clear() {
            stack.undo.length = 0;
            stack.redo.length = 0;
        }
        function action(event, source, dest) {
            if (event) {
                if (event.defaultPrevented)
                    return;
                event.preventDefault();
            }
            if (stack[source].length === 0)
                return;
            const entry = stack[source].pop();
            stack[dest].push(entry);
            cutoff();
            ignoreChange = true;
            if (typeof entry[source] === 'function') {
                entry[source]();
            }
            else {
                editor.updateContents(entry[source], SOURCE_USER$1, entry[source + 'Selection']);
            }
            ignoreChange = false;
        }
        function record(change, oldContents, selection, oldSelection) {
            const timestamp = Date.now();
            const action = getAction(change);
            stack.redo.length = 0;
            let undoChange = change.invert(oldContents);
            if (!action || lastAction !== action)
                cutoff();
            lastAction = action;
            if (lastRecorded && (!delay || lastRecorded + delay > timestamp) && stack.undo.length > 0) {
                const entry = stack.undo.pop();
                if (entry) {
                    oldSelection = entry.undoSelection;
                    undoChange = undoChange.compose(entry.undo);
                    change = entry.redo.compose(change);
                }
            }
            else {
                lastRecorded = timestamp;
            }
            stack.undo.push({
                redo: change,
                undo: undoChange,
                redoSelection: selection,
                undoSelection: oldSelection,
            });
            if (stack.undo.length > maxStack) {
                stack.undo.shift();
            }
        }
        function transform(change) {
            stack.undo.forEach(entry => {
                entry.undo = change.transform(entry.undo, true);
                entry.redo = change.transform(entry.redo, true);
            });
            stack.redo.forEach(entry => {
                entry.undo = change.transform(entry.undo, true);
                entry.redo = change.transform(entry.redo, true);
            });
        }
        function onTextChange({ change, oldContents, selection, oldSelection, source }) {
            if (ignoreChange)
                return;
            if (source === SOURCE_USER$1) {
                record(change, oldContents, selection, oldSelection);
            }
            else if (source !== SOURCE_SILENT) {
                transform(change);
            }
        }
        function onSelectionChange({ change }) {
            if (change)
                return;
            cutoff();
        }
        editor.on('text-change', onTextChange);
        editor.on('selection-change', onSelectionChange);
        root.addEventListener('shortcut:Mod+Z', undo);
        root.addEventListener('shortcut:win:Ctrl+Y', redo);
        root.addEventListener('shortcut:mac:Cmd+Shift+Z', redo);
        return {
            hasUndo,
            hasRedo,
            undo,
            redo,
            cutoff,
            clear,
            onDestroy() {
                editor.off('text-change', onTextChange);
                editor.off('selection-change', onSelectionChange);
                root.removeEventListener('shortcut:Mod+Z', undo);
                root.removeEventListener('shortcut:win:Ctrl+Y', redo);
                root.removeEventListener('shortcut:mac:Cmd+Shift+Z', redo);
            }
        };
    };
}
function newStack() {
    return {
        undo: [],
        redo: [],
    };
}
function getAction(change) {
    if (change.ops.length === 1 || change.ops.length === 2 && change.ops[0].retain && !change.ops[0].attributes) {
        const changeOp = change.ops[change.ops.length - 1];
        if (changeOp.delete)
            return 'delete';
        if (changeOp.insert === '\n')
            return 'newline';
        if (changeOp.insert)
            return 'insert';
    }
    return '';
}

function placeholder(placeholder) {
    return (editor, root) => {
        function onDecorate(event) {
            const decorators = event.detail;
            if (editor.length === 1) {
                const text = (typeof placeholder === 'function' ? placeholder() : placeholder) || '';
                decorators.embed(0, { class: 'placeholder', 'data-placeholder': text });
            }
        }
        root.addEventListener('decorate', onDecorate);
        return {
            onDestroy() {
                root.removeEventListener('decorate', onDecorate);
            }
        };
    };
}

const SOURCE_USER$2 = 'user';
const blockReplacements = [
    [/^(#{1,6}) $/, capture => ({ header: capture.length })],
    [/^[-*] $/, () => ({ list: 'bullet' })],
    [/^1\. $/, () => ({ list: 'ordered' })],
    [/^([AaIi])\. $/, type => ({ list: 'ordered', type })],
    [/^(-?\d+)\. $/, start => ({ list: 'ordered', start })],
    [/^([A-Z])\. $/, char => ({ list: 'ordered', type: 'A', start: char.charCodeAt(0) - 'A'.charCodeAt(0) + 1 })],
    [/^([a-z])\. $/, char => ({ list: 'ordered', type: 'a', start: char.charCodeAt(0) - 'a'.charCodeAt(0) + 1 })],
    [/^([IVXLCDM]+)\. $/i, chars => ({ list: 'ordered', type: chars[0].toUpperCase() === chars[0] ? 'I' : 'i', start: fromRomanNumeral(chars) })],
    [/^> $/, () => ({ blockquote: true })],
];
const textReplacements = [
    [/--$/, () => '—'],
    [/\.\.\.$/, () => '…'],
];
function blockReplace(editor, index, prefix, paper) {
    return blockReplacements.some(([regexp, getAttributes]) => {
        const match = prefix.match(regexp);
        if (match) {
            const attributes = getAttributes(match[1]);
            if (!paper.blocks.findByAttributes(attributes)) {
                return false;
            }
            const change = editor.getChange(() => {
                editor.formatLine([index, index], attributes);
                editor.deleteText([index - prefix.length, index]);
            });
            const end = index - prefix.length;
            editor.updateContents(change, SOURCE_USER$2, [end, end]);
            return true;
        }
        else {
            return false;
        }
    });
}
function textReplace(editor, index, prefix) {
    return textReplacements.some(([regexp, replaceWith]) => {
        const match = prefix.match(regexp);
        if (match) {
            editor.insertText([index - match[0].length, index], replaceWith(match[1]), undefined, SOURCE_USER$2);
            return true;
        }
        else {
            return false;
        }
    });
}
const defaultHandlers = [blockReplace, textReplace];
function smartEntry (handlers = defaultHandlers) {
    return (editor, root, paper) => {
        let ignore = false;
        function onTextChange({ change, source }) {
            if (ignore || source !== 'user' || !editor.selection || !isTextEntry(change))
                return;
            const index = editor.selection[1];
            const text = editor.getExactText();
            const lineStart = text.lastIndexOf('\n', index - 1) + 1;
            const prefix = text.slice(lineStart, index);
            ignore = true;
            handlers.some(handler => handler(editor, index, prefix, paper));
            ignore = false;
        }
        editor.on('text-change', onTextChange);
        return {
            onDestroy() {
                editor.off('text-change', onTextChange);
            }
        };
    };
}
function isTextEntry(change) {
    return (change.ops.length === 1 ||
        (change.ops.length === 2 && change.ops[0].retain && !change.ops[0].attributes)) &&
        change.ops[change.ops.length - 1].insert &&
        change.ops[change.ops.length - 1].insert !== '\n';
}
const DIGIT_VALUES = {
    I: 1,
    V: 5,
    X: 10,
    L: 50,
    C: 100,
    D: 500,
    M: 1000
};
function fromRomanNumeral(romanNumeral) {
    romanNumeral = romanNumeral.toUpperCase();
    let result = 0;
    for (let i = 0; i < romanNumeral.length; i++) {
        const currentLetter = DIGIT_VALUES[romanNumeral[i]];
        const nextLetter = DIGIT_VALUES[romanNumeral[i + 1]];
        if (currentLetter === undefined)
            return undefined;
        if (currentLetter < nextLetter) {
            result += nextLetter - currentLetter;
            i++;
        }
        else {
            result += currentLetter;
        }
    }
    return result;
}

function smartQuotes() {
    return (editor) => {
        function onTextChange({ change, source, selection }) {
            if (source !== SOURCE_USER$3 || !editor.selection || !isTextEntry$1(change))
                return;
            const index = editor.selection[1];
            const lastOp = change.ops[change.ops.length - 1];
            if (typeof lastOp.insert !== 'string')
                return;
            const lastChars = editor.getText([index - 1, index]) + lastOp.insert.slice(-1);
            const replaced = lastChars.replace(/(?:^|[\s\{\[\(\<'"\u2018\u201C])(")$/, '“')
                .replace(/"$/, '”')
                .replace(/(?:^|[\s\{\[\(\<'"\u2018\u201C])(')$/, '‘')
                .replace(/'$/, '’');
            if (replaced !== lastChars) {
                const quote = replaced.slice(-1);
                lastOp.insert = lastOp.insert.slice(0, -1) + quote;
                editor.updateContents(change, source, selection);
                return false;
            }
        }
        function onPaste(event) {
            let originalText = event.delta
                ? event.delta.ops.reduce((txt, op) => txt + (typeof op.insert === 'string' ? op.insert : ' '), '')
                : event.text || '';
            const text = originalText.replace(/(^|\s)"/g, '$1“')
                .replace(/"($|\s)/g, '”$1')
                .replace(/"($|[\s,.!])/g, '”$1')
                .replace(/\b'/g, '’')
                .replace(/'\b/g, '‘');
            if (event.delta) {
                const quotes = new Delta();
                let pos = 0;
                for (let i = 0; i < originalText.length; i++) {
                    if (originalText[i] !== text[i]) {
                        quotes.retain(i - pos).delete(1).insert(text[i]);
                        pos = i + 1;
                    }
                }
                const trailingWhitespace = new Delta();
                pos = 0;
                text.replace(/ +\n/g, (text, offset) => {
                    trailingWhitespace.retain(offset - pos).delete(text.length - 1);
                    pos = offset + text.length - 1;
                    return '\n';
                });
                event.delta = event.delta.compose(quotes).compose(trailingWhitespace);
            }
            else {
                event.text = text.replace(/ +\n/g, '\n');
            }
        }
        editor.on('text-changing', onTextChange);
        editor.on('paste', onPaste);
        return {
            onDestroy() {
                editor.off('text-changing', onTextChange);
                editor.off('paste', onPaste);
            }
        };
    };
}
function isTextEntry$1(change) {
    return !!(change.ops.length === 1 ||
        (change.ops.length === 2 && change.ops[0].retain && !change.ops[0].attributes)) &&
        typeof change.ops[change.ops.length - 1].insert === 'string' &&
        change.ops[change.ops.length - 1].insert !== '\n';
}

export { history, input, keyShortcuts, placeholder, shortcuts, smartEntry, smartQuotes };
//# sourceMappingURL=index.esm.js.map
